import {
    Material,
    Mesh,
    type BufferGeometry,
    type Group,
    type Object3D,
} from 'three'
import { SkeletonUtils } from 'three-stdlib'
import OwnedGLBResources from './OwnedGLBResources'
import {
    createMaterialVariantController,
} from './createMaterialVariantController'
import type {
    GLBModel,
    GLTFSource,
    OwnedGLBInstance,
    SourceMaterialVariantTable,
} from './types'

type MaterialSelection = Material | Material[]
type MaterialMesh = Mesh<BufferGeometry, MaterialSelection>

const traverseScenePair = (
    source:Object3D,
    owned:Object3D,
    callback:(source:Object3D, owned:Object3D) => void,
) => {
    callback(source, owned)
    source.children.forEach((sourceChild, index) => {
        traverseScenePair(
            sourceChild,
            owned.children[index],
            callback,
        )
    })
}

const cloneMaterialVariantTable = (
    source:Object3D,
    owned:Object3D,
    resources:OwnedGLBResources,
) => {
    const sourceTable = source.userData.variantMaterials as (
        SourceMaterialVariantTable | undefined
    )
    if(!sourceTable) return

    const ownedTable:SourceMaterialVariantTable = Object.fromEntries(
        Object.entries(sourceTable).map(([name, entry]) => [
            name,
            {
                gltfMaterialIndex: entry.gltfMaterialIndex,
                material: entry.material
                    ? resources.ownMaterial(entry.material)
                    : null,
            },
        ]),
    )
    owned.userData.variantMaterials = ownedTable

    const ownedMesh = owned instanceof Mesh
        ? owned as MaterialMesh
        : undefined
    const ownedMaterial = (
        ownedMesh
        && !Array.isArray(ownedMesh.material)
            ? ownedMesh.material
            : undefined
    )
    const sourceOriginalMaterial = (
        source.userData as Record<string, unknown>
    ).originalMaterial
    owned.userData.originalMaterial = (
        sourceOriginalMaterial instanceof Material
            ? resources.ownMaterial(sourceOriginalMaterial)
            : ownedMaterial
    )
}

/**
 * Clone one immutable cached GLTF prototype into a mutable viewer instance.
 * Repeated source materials/textures remain shared inside that instance but
 * never across two viewers.
 */
export const createOwnedGLBInstance = (
    source:GLTFSource,
):OwnedGLBInstance => {
    const resources = new OwnedGLBResources()
    const scene = SkeletonUtils.clone(source.scene) as Group

    traverseScenePair(source.scene, scene, (sourceObject, ownedObject) => {
        if(
            sourceObject instanceof Mesh
            && ownedObject instanceof Mesh
        ) {
            const sourceMesh = sourceObject as MaterialMesh
            const ownedMesh = ownedObject as MaterialMesh
            ownedMesh.material = resources.ownMaterialSelection(
                sourceMesh.material,
            )
            ownedMesh.castShadow = true
            ownedMesh.receiveShadow = true
        }
        cloneMaterialVariantTable(
            sourceObject,
            ownedObject,
            resources,
        )
    })

    const model:GLBModel = {
        scene: scene as GLBModel['scene'],
        variants: createMaterialVariantController(
            scene,
            source.functions,
            resources,
        ),
    }

    return {
        model,
        dispose: () => resources.dispose(scene),
    }
}
